import React, { useState, useMemo, useEffect, useCallback } from 'react';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import useModal from './hooks/useModal';
import { CollateralModalProps } from './CollateralModal/types';
import MarketIcon from '../Utils/MarketIcon';
import FunXyzAssetIcon from '../Utils/FunXyzAssetIcon';
import QRCode from 'qrcode.react';
import { Checkmark, Copy, Warning } from '@velocity-exchange/icons';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import Select from 'src/components/primitives/Select/Select';
import InputUi from 'src/components/primitives/Input/InputUi';
import { ComboBox } from 'src/components/primitives/ComboBox/ComboBox';
import useFunXyzActions, {
	SOLANA_TARGET_ASSETS,
	SupportedTargetAsset,
	FunXyzAsset,
} from 'src/hooks/useFunXyzActions';
import InlineLoadingBar from 'src/components/InlineLoadingBar/InlineLoadingBar';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import Divider from 'src/components/primitives/Divider/Divider';
import Button from 'src/components/primitives/Button/Button';
import { cj } from 'src/utils/classNames';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import { MAIN_POOL_ID, UIMarket } from '@velocity-exchange/common';
import { useAccountCreationCost } from 'src/hooks/useAccountCreationCost';
import { getCleanedMarketSymbol } from 'src/utils/market';
import SubAccountSelector from './CollateralModal/components/SubAccountSelector';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import useTickedAllSubAccounts from 'src/hooks/useTickedAllSubAccounts';
import { getChainIconSymbol, isAssetAllowed } from 'src/constants/funXyzChains';
import Collapsible from 'src/components/Collapsible/Collapsible';
import Chevron from 'src/components/Icons/Chevron';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';

interface AssetOption {
	symbol: string;
	name: string;
	logo?: string;
	availableChainIds: string[];
	/** chainId -> token contract address for Covalent logo fallback */
	addressByChainId: Record<string, string>;
}

interface ChainOption {
	id: string;
	name: string;
}

// Receive assets on Solana (what the user gets on Velocity)
const RECEIVE_ASSETS = [
	{ symbol: 'USDC', name: 'USD Coin' },
	{ symbol: 'SOL', name: 'Solana' },
];

export interface DepositFromOtherSourcesModalProps extends CollateralModalProps {
	createNewSubaccount?: boolean;
}

const DepositFromOtherSourcesModal = ({
	collateralIndex,
	collateralPoolId,
	createNewSubaccount,
	targetAccountKey,
	defaultLargestBalance,
}: DepositFromOtherSourcesModalProps) => {
	const { hide, show } = useModal();
	const [copied, setCopied] = useState(false);
	const [isHowToDepositExpanded, setIsHowToDepositExpanded] = useState(false);
	const isWalletConnected = useWalletIsConnected();
	const [assetSearchQuery, setAssetSearchQuery] = useState('');
	const [assetDropdownOpen, setAssetDropdownOpen] = useState(false);
	const { captureEvent } = usePostHogCapture();
	const userAccountNotInitialized = useVelocityAccountsStore(
		(s) => s.userAccountNotInitialized
	);
	const { baseAccountRent } = useAccountCreationCost();
	const currentUserKey = useVelocityAccountsStore((s) => s.currentUserKey);
	const { isPrivyNativeAuth } = usePrivyWalletContext();

	const [selectedTargetAccountKey, setSelectedTargetAccountKey] = useState(
		createNewSubaccount ? '' : (targetAccountKey ?? currentUserKey ?? '')
	);

	const selectedTargetAccount = useVelocityAccountsStore(
		(s) => s.accounts[selectedTargetAccountKey ?? '']
	);

	const selectedPoolId =
		selectedTargetAccount?.poolId ?? collateralPoolId ?? MAIN_POOL_ID;

	const poolId = (() => {
		if (userAccountNotInitialized) {
			return MAIN_POOL_ID;
		}

		return selectedTargetAccount?.poolId ?? collateralPoolId ?? null;
	})();

	// Only show receive assets that exist in the target pool; Fun.xyz supports USDC and SOL on Solana
	const availableReceiveAssets = useMemo(() => {
		if (poolId === null) return [];
		const poolSymbols = new Set(
			UIMarket.spotMarkets
				.filter((m) => m.poolId === poolId)
				.map((m) => getCleanedMarketSymbol(m.symbol))
		);
		return RECEIVE_ASSETS.filter((ra) => poolSymbols.has(ra.symbol));
	}, [poolId]);

	const hasSingleReceiveAsset = availableReceiveAssets.length === 1;

	const filterAccountForFunXyz = useCallback(
		(account: { poolId: number }) => account.poolId === MAIN_POOL_ID,
		[]
	);

	const allSubaccounts = useTickedAllSubAccounts();
	const eligibleAccounts = useMemo(
		() => allSubaccounts.filter(filterAccountForFunXyz),
		[allSubaccounts, filterAccountForFunXyz]
	);

	useEffect(() => {
		if (
			eligibleAccounts.length === 0 ||
			eligibleAccounts.some((a) => a.userKey === selectedTargetAccountKey)
		)
			return;
		setSelectedTargetAccountKey(eligibleAccounts[0].userKey);
	}, [eligibleAccounts, selectedTargetAccountKey]);

	// Fun.xyz UDA hook - pass targetAccountKey so deposit address is for the selected account, not active
	const {
		supportedAssets,
		chains,
		createDepositAddress,
		isCreatingAddress,
		receivingAddress,
		selectedSourceAsset,
		isLoadingAssets,
		loadSupportedAssets,
		fetchQuote,
		quote,
		isGettingQuote,
	} = useFunXyzActions({
		targetAccountKey: createNewSubaccount
			? undefined
			: selectedTargetAccountKey,
		forNewSubaccount: createNewSubaccount,
	});

	// Selection state
	const [selectedAssetSymbol, setSelectedAssetSymbol] = useState<string | null>(
		null
	);
	const [selectedChainId, setSelectedChainId] = useState<string | null>(null);
	const [selectedReceiveAsset, setSelectedReceiveAsset] = useState(
		RECEIVE_ASSETS[0]
	);

	// Build unique asset list across all chains (deduplicated by symbol)
	// Only include whitelisted assets, USDC goes first, then sort alphabetically
	const uniqueAssets = useMemo(() => {
		const assetMap = new Map<string, AssetOption>();

		for (const asset of supportedAssets) {
			// Skip assets not in the whitelist for their chain
			if (!isAssetAllowed(asset.chainId, asset.symbol)) {
				continue;
			}

			// Normalize symbol for deduplication (case-insensitive)
			const normalizedSymbol = asset.symbol.toUpperCase();
			const existing = assetMap.get(normalizedSymbol);

			if (existing) {
				if (!existing.availableChainIds.includes(asset.chainId)) {
					existing.availableChainIds.push(asset.chainId);
				}
				if (asset.address) {
					existing.addressByChainId[asset.chainId] = asset.address;
				}
				if (!existing.logo && asset.logo) {
					existing.logo = asset.logo;
				}
			} else {
				assetMap.set(normalizedSymbol, {
					symbol: asset.symbol,
					name: asset.name,
					logo: asset.logo,
					availableChainIds: [asset.chainId],
					addressByChainId: asset.address
						? { [asset.chainId]: asset.address }
						: {},
				});
			}
		}

		const assets = Array.from(assetMap.values());

		// Sort: USDC first, then alphabetically
		assets.sort((a, b) => {
			if (a.symbol.toUpperCase() === 'USDC') return -1;
			if (b.symbol.toUpperCase() === 'USDC') return 1;
			return a.symbol.localeCompare(b.symbol);
		});

		return assets;
	}, [supportedAssets]);

	// Build chain list with availability info based on selected asset
	const chainOptions = useMemo(() => {
		const selectedAsset = uniqueAssets.find(
			(a) => a.symbol === selectedAssetSymbol
		);
		const availableChainIds = new Set(selectedAsset?.availableChainIds ?? []);

		const chainList: (ChainOption & { isAvailable: boolean })[] = chains.map(
			(chain) => ({
				id: chain.chainId,
				name: chain.chainName,
				isAvailable: availableChainIds.has(chain.chainId),
			})
		);

		// Sort: available chains first, then unavailable
		chainList.sort((a, b) => {
			if (a.isAvailable && !b.isAvailable) return -1;
			if (!a.isAvailable && b.isAvailable) return 1;
			return a.name.localeCompare(b.name);
		});

		return chainList;
	}, [chains, selectedAssetSymbol, uniqueAssets]);

	// Filter assets by search query - only match ticker/symbol, not asset name
	const filteredAssets = useMemo(() => {
		if (!assetSearchQuery) return uniqueAssets;
		const query = assetSearchQuery.toLowerCase().trim();
		return uniqueAssets.filter((asset) =>
			asset.symbol.toLowerCase().includes(query)
		);
	}, [uniqueAssets, assetSearchQuery]);

	// Get selected asset object
	const selectedAsset = useMemo(() => {
		return uniqueAssets.find((a) => a.symbol === selectedAssetSymbol) ?? null;
	}, [uniqueAssets, selectedAssetSymbol]);

	// Get selected chain object
	const selectedChain = useMemo(() => {
		return chainOptions.find((c) => c.id === selectedChainId) ?? null;
	}, [chainOptions, selectedChainId]);

	// Load supported assets on mount
	useEffect(() => {
		loadSupportedAssets();
	}, [loadSupportedAssets]);

	// Set default asset (USDC) when assets load
	useEffect(() => {
		if (uniqueAssets.length > 0 && !selectedAssetSymbol) {
			// Default to USDC if available, otherwise first asset
			const defaultAsset =
				uniqueAssets.find((a) => a.symbol === 'USDC') ?? uniqueAssets[0];
			setSelectedAssetSymbol(defaultAsset.symbol);
		}
	}, [uniqueAssets, selectedAssetSymbol]);

	// Keep selected receive asset in sync with pool's available assets
	useEffect(() => {
		const current = selectedReceiveAsset.symbol;
		const isAvailable = availableReceiveAssets.some(
			(a) => a.symbol === current
		);
		if (!isAvailable && availableReceiveAssets[0]) {
			setSelectedReceiveAsset(availableReceiveAssets[0]);
		}
	}, [availableReceiveAssets, selectedReceiveAsset.symbol]);

	// Set default chain when asset changes (prefer ETH, then first available)
	useEffect(() => {
		if (!selectedAsset) return;

		// If current chain is available for the new asset, keep it
		if (
			selectedChainId &&
			selectedAsset.availableChainIds.includes(selectedChainId)
		) {
			return;
		}

		// Prefer Ethereum (chainId '1') if available for this asset
		const ethChainId = '1';
		if (selectedAsset.availableChainIds.includes(ethChainId)) {
			setSelectedChainId(ethChainId);
			return;
		}

		// Otherwise, select the first available chain
		const firstAvailableChain = chainOptions.find((c) => c.isAvailable);
		if (firstAvailableChain) {
			setSelectedChainId(firstAvailableChain.id);
		}
	}, [selectedAsset, selectedChainId, chainOptions]);

	// Create deposit address when selections change
	useEffect(() => {
		if (
			!isWalletConnected ||
			!selectedReceiveAsset ||
			!selectedAssetSymbol ||
			!selectedChainId ||
			availableReceiveAssets.length === 0 ||
			!availableReceiveAssets.some(
				(a) => a.symbol === selectedReceiveAsset.symbol
			)
		)
			return;

		const targetAssetKey = selectedReceiveAsset.symbol as SupportedTargetAsset;
		if (!SOLANA_TARGET_ASSETS[targetAssetKey]) return;

		// Find the original FunXyzAsset for the selected asset + chain combo
		const funXyzAsset = supportedAssets.find(
			(a) =>
				a.symbol === selectedAssetSymbol &&
				String(a.chainId) === String(selectedChainId)
		);

		if (funXyzAsset) {
			void (async () => {
				try {
					await createDepositAddress(targetAssetKey, funXyzAsset);
				} catch (err) {
					console.error('Failed to create deposit address:', err);
				}
			})();
		}
	}, [
		isWalletConnected,
		selectedReceiveAsset?.symbol,
		selectedAssetSymbol,
		selectedChainId,
		availableReceiveAssets,
		createDepositAddress,
		supportedAssets,
	]);

	// Fetch quote for $100k worth of the asset to show price impact
	useEffect(() => {
		if (
			!receivingAddress ||
			!selectedReceiveAsset ||
			!selectedAssetSymbol ||
			!selectedChainId
		)
			return;

		const targetAssetKey = selectedReceiveAsset.symbol as SupportedTargetAsset;
		const targetConfig = SOLANA_TARGET_ASSETS[targetAssetKey];
		if (!targetConfig) return;

		// Find the source asset
		const sourceAsset = supportedAssets.find(
			(a) => a.symbol === selectedAssetSymbol && a.chainId === selectedChainId
		);
		if (!sourceAsset) return;

		// Calculate $100k worth in base units
		// For most tokens, we estimate based on common prices
		const QUOTE_AMOUNT_USD = 100000;
		const getBaseUnitsFor100k = (asset: FunXyzAsset): string => {
			const decimals = asset.decimals;
			// Stablecoins: $1 each
			if (
				[
					'USDC',
					'USDT',
					'DAI',
					'BUSD',
					'USDP',
					'USDS',
					'USDbC',
					'USDe',
				].includes(asset.symbol)
			) {
				return (BigInt(QUOTE_AMOUNT_USD) * BigInt(10 ** decimals)).toString();
			}
			// ETH/WETH: ~$3000
			if (['ETH', 'WETH'].includes(asset.symbol)) {
				const amount = Math.floor(QUOTE_AMOUNT_USD / 3000);
				return (BigInt(amount) * BigInt(10 ** decimals)).toString();
			}
			// BTC/WBTC: ~$100000
			if (['BTC', 'WBTC', 'cbBTC', 'BTCB'].includes(asset.symbol)) {
				const amount = Math.floor(QUOTE_AMOUNT_USD / 100000);
				return (
					BigInt(Math.max(amount, 1)) * BigInt(10 ** decimals)
				).toString();
			}
			// SOL: ~$200
			if (['SOL'].includes(asset.symbol)) {
				const amount = Math.floor(QUOTE_AMOUNT_USD / 200);
				return (BigInt(amount) * BigInt(10 ** decimals)).toString();
			}
			// Default: assume $1 per token (conservative for smaller tokens)
			return (BigInt(QUOTE_AMOUNT_USD) * BigInt(10 ** decimals)).toString();
		};

		const fromAmountBaseUnit = getBaseUnitsFor100k(sourceAsset);

		fetchQuote({
			fromAmountBaseUnit,
			fromChainId: selectedChainId,
			fromTokenAddress: sourceAsset.address,
			recipientAddress: receivingAddress,
			toChainId: '1151111081099710', // Solana
			toTokenAddress: targetConfig.address,
		}).catch(() => {
			// Silently fail - we'll show a fallback in the UI
		});
	}, [
		receivingAddress,
		selectedReceiveAsset?.symbol,
		selectedAssetSymbol,
		selectedChainId,
		supportedAssets,
		fetchQuote,
	]);

	// Normally we'd just let `BaseModal` handle going back internally; however,
	// we want to keep some props synced across modals, so we need to explicitly set
	// it here. Since this modal can only be the second and final node in the list
	// `show` is essentially the same as a pop, so it should be okay (for now).
	const handleBack = () => {
		if (createNewSubaccount) {
			show({
				type: 'new_subaccount',
				props: { collateralIndex, collateralPoolId },
			});

			return;
		}

		show({
			type: 'deposit',
			props: {
				collateralIndex,
				collateralPoolId,
				targetAccountKey: selectedTargetAccountKey,
				defaultLargestBalance,
			},
		});
	};

	const handleCopy = async () => {
		if (!receivingAddress) return;

		try {
			await navigator.clipboard.writeText(receivingAddress);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);

			// Track the copy action with route details
			captureEvent('collateral_funxyz_address_copied', {
				from_chain_id: selectedChainId,
				from_chain_name: selectedChain?.name,
				from_asset_symbol: selectedAssetSymbol,
				to_chain: 'Solana',
				to_asset_symbol: selectedReceiveAsset.symbol,
				receiving_address: receivingAddress,
			});
		} catch (error) {
			console.error('Failed to copy:', error);
		}
	};

	const handleTargetAccountChange = useCallback(
		(newAccountKey: string) => setSelectedTargetAccountKey(newAccountKey),
		[]
	);

	const handleChainSelect = (chainId: string) => {
		const chain = chainOptions.find((c) => c.id === chainId);
		// Only allow selection if chain is available for selected asset
		if (chain?.isAvailable) {
			setSelectedChainId(chainId);
		}
	};

	const chainMatchesAddress =
		!selectedChainId ||
		!selectedSourceAsset ||
		selectedChainId === selectedSourceAsset.chainId;
	const isAddressLoading =
		isCreatingAddress || !receivingAddress || !chainMatchesAddress;

	const title = (() => {
		if (!isPrivyNativeAuth) {
			return 'Deposit from Other Sources';
		}

		if (userAccountNotInitialized) {
			return 'Deposit & Create Account';
		}

		return 'Deposit';
	})();

	return (
		<BaseModal.Container
			onClose={hide}
			className="sm:max-w-[700px] sm:min-w-[480px]"
		>
			<BaseModal.Title onClose={hide} onBack={handleBack}>
				{title}
			</BaseModal.Title>
			<BaseModal.Body>
				<div className="relative">
					{/* How to Deposit expandable section */}
					<Collapsible.Root>
						<Collapsible.Trigger
							className="flex w-fit items-center gap-x-xs hover:text-text-default"
							onClick={() => setIsHowToDepositExpanded(!isHowToDepositExpanded)}
						>
							How to Deposit
							<Chevron direction={isHowToDepositExpanded ? 'up' : 'down'} />
						</Collapsible.Trigger>
						<Collapsible.Content>
							<div className="bg-container-bg-secondary flex flex-col gap-3 rounded-sm p-md pb-0">
								{!createNewSubaccount && (
									<div className="flex gap-2">
										<div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-text-tertiary">
											<span className="typo-body-2xs font-bold text-container-bg">
												1
											</span>
										</div>
										<div className="flex flex-col gap-1">
											<span className="text-left font-semibold text-text-default">
												Select Deposit Account
											</span>
											<span className="text-left typo-body-2xs">
												Choose which Velocity account to receive the funds.
											</span>
										</div>
									</div>
								)}

								<div className="flex gap-2">
									<div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-text-tertiary">
										<span className="typo-body-2xs font-bold text-container-bg">
											{createNewSubaccount ? '1' : '2'}
										</span>
									</div>
									<div className="flex flex-col gap-1">
										<span className="text-left font-semibold text-text-default">
											Select Transfer Asset
										</span>
										<span className="text-left typo-body-2xs">
											Choose the asset and chain you want to transfer from.
										</span>
									</div>
								</div>

								<div className="flex gap-2">
									<div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-text-tertiary">
										<span className="typo-body-2xs font-bold text-container-bg">
											{createNewSubaccount ? '2' : '3'}
										</span>
									</div>
									<div className="flex flex-col gap-1">
										<span className="text-left font-semibold text-text-default">
											Select Receiving Asset
										</span>
										<span className="text-left typo-body-2xs">
											{hasSingleReceiveAsset
												? 'Only assets available in your target pool can be received.'
												: 'Pick the asset to receive in your Velocity account (supports USDC and SOL).'}
										</span>
									</div>
								</div>

								<div className="flex gap-2">
									<div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-text-tertiary">
										<span className="typo-body-2xs font-bold text-container-bg">
											{createNewSubaccount ? '3' : '4'}
										</span>
									</div>
									<div className="flex flex-col gap-1">
										<span className="text-left font-semibold text-text-default">
											Transfer & Receive
										</span>
										<span className="text-left typo-body-2xs">
											Send the asset to the receiving address. Funds arrive in
											your Velocity account within 1 minute.
										</span>
									</div>
								</div>
							</div>
						</Collapsible.Content>
					</Collapsible.Root>

					<DefinitionTooltip
						definitionId="depositFromOtherSourcesMinimumDeposit"
						className="absolute top-0 right-0 inline-flex items-center gap-x-xs text-star-yellow"
					>
						<Warning size={16} />
						Minimum deposit: $30
					</DefinitionTooltip>
				</div>

				<Divider />

				{/* Deposit To */}
				{!createNewSubaccount && (
					<div className="flex flex-col gap-2">
						<span className="typo-label-xs text-text-default">
							1. Deposit To
						</span>
						<SubAccountSelector
							mode="deposit"
							accountLabel="Account"
							selectedTargetAccountKey={selectedTargetAccountKey}
							selectedPoolId={selectedPoolId}
							handleTargetAccountChange={handleTargetAccountChange}
							newSubaccount={{ isNewAccount: false }}
							filterAccount={filterAccountForFunXyz}
							includeDelegates
						/>
					</div>
				)}

				{/* Deposit From */}
				<div className="flex flex-col gap-2">
					<span className="typo-label-xs text-text-default">
						{createNewSubaccount ? '1' : '2'}. Deposit From
					</span>

					{isLoadingAssets ? (
						<div className="flex items-center justify-center py-4">
							<InlineLoadingBar />
						</div>
					) : (
						<div className="grid grid-cols-2 gap-x-sm">
							{/* Asset Selector with Search */}
							<ComboBox.Root
								open={assetDropdownOpen}
								onOpenChange={setAssetDropdownOpen}
							>
								<ComboBox.Trigger label="Asset" disabled={!selectedAsset}>
									{selectedAsset && (
										<>
											<FunXyzAssetIcon
												logo={selectedAsset.logo}
												symbol={selectedAsset.symbol}
												chainId={
													selectedChainId ??
													Object.keys(selectedAsset.addressByChainId)[0]
												}
												address={
													selectedChainId
														? selectedAsset.addressByChainId[selectedChainId]
														: Object.values(selectedAsset.addressByChainId)[0]
												}
											/>
											{selectedAsset.symbol}
										</>
									)}
								</ComboBox.Trigger>
								<ComboBox.Options
									id="deposit-asset-combobox"
									query={assetSearchQuery}
									setQuery={setAssetSearchQuery}
									value={selectedAssetSymbol ?? ''}
									onValueChange={(symbol) => {
										setSelectedAssetSymbol(symbol);
										setAssetDropdownOpen(false);
									}}
									searchBarPlaceholder="Search assets..."
									align="start"
									sideOffset={8}
									className="w-[240px]"
									scrollContainerClassName="max-h-[280px]"
								>
									{filteredAssets.map((asset) => (
										<ComboBox.Option
											key={asset.symbol}
											value={asset.symbol}
											isSelected={asset.symbol === selectedAssetSymbol}
											onValueChange={() => {
												setSelectedAssetSymbol(asset.symbol);
												setAssetDropdownOpen(false);
											}}
										>
											<div className="flex items-center gap-x-xs">
												<FunXyzAssetIcon
													logo={asset.logo}
													symbol={asset.symbol}
													chainId={
														(selectedChainId &&
														asset.addressByChainId[selectedChainId]
															? selectedChainId
															: Object.keys(asset.addressByChainId)[0]) ??
														undefined
													}
													address={
														(selectedChainId &&
															asset.addressByChainId[selectedChainId]) ??
														Object.values(asset.addressByChainId)[0]
													}
												/>
												<span>{asset.symbol}</span>
											</div>
										</ComboBox.Option>
									))}
								</ComboBox.Options>
							</ComboBox.Root>

							{/* Chain Selector */}
							<Select.Root
								value={selectedChainId ?? ''}
								onValueChange={handleChainSelect}
							>
								<Select.Trigger label="Chain">
									{selectedChain && (
										<>
											<MarketIcon
												marketSymbol={
													getChainIconSymbol(selectedChain.id) ?? ''
												}
												className="size-lg shrink-0"
											/>
											<span className="truncate">{selectedChain.name}</span>
										</>
									)}
								</Select.Trigger>
								<Select.Options
									className="max-h-[280px] w-[240px]"
									align="start"
								>
									{chainOptions.map((chain) => (
										<Select.Option
											key={chain.id}
											value={chain.id}
											disabled={!chain.isAvailable}
										>
											<div className="flex items-center gap-x-xs">
												<MarketIcon
													marketSymbol={getChainIconSymbol(chain.id) ?? ''}
													className={cj(
														'size-lg shrink-0',
														!chain.isAvailable && 'opacity-50'
													)}
												/>
												<span className="truncate">{chain.name}</span>
											</div>
										</Select.Option>
									))}
								</Select.Options>
							</Select.Root>
						</div>
					)}
				</div>

				{/* Receive As */}
				<div className="flex flex-col gap-2">
					<span className="typo-label-xs text-text-default">
						{createNewSubaccount ? '2' : '3'}. Receive in Velocity Account as
					</span>

					{availableReceiveAssets.length === 0 ? (
						<div className="rounded-sm border border-input-border bg-input-bg p-md">
							Your selected account&apos;s pool does not support USDC or SOL.
							Please select a different account to receive deposits.
						</div>
					) : hasSingleReceiveAsset && availableReceiveAssets[0] ? (
						<InputUi.Container disabled>
							<InputUi.InsetLabel size="sm">Asset</InputUi.InsetLabel>
							<InputUi.Value
								size="sm"
								className="flex items-center justify-end gap-x-xs"
							>
								<MarketIcon
									marketSymbol={availableReceiveAssets[0].symbol}
									className="size-lg shrink-0"
								/>
								{availableReceiveAssets[0].symbol}
							</InputUi.Value>
						</InputUi.Container>
					) : (
						<Select.Root
							value={selectedReceiveAsset.symbol}
							onValueChange={(symbol) => {
								const asset = availableReceiveAssets.find(
									(a) => a.symbol === symbol
								);
								if (asset) setSelectedReceiveAsset(asset);
							}}
						>
							<Select.Trigger label="Asset">
								<div className="flex items-center gap-x-xs">
									<MarketIcon
										marketSymbol={selectedReceiveAsset.symbol}
										className="size-lg shrink-0"
									/>
									{selectedReceiveAsset.symbol}
								</div>
							</Select.Trigger>
							<Select.Options className="w-[200px]" align="end">
								{availableReceiveAssets.map((asset) => (
									<Select.Option key={asset.symbol} value={asset.symbol}>
										<div className="flex items-center gap-x-xs">
											<MarketIcon
												marketSymbol={asset.symbol}
												className="size-lg shrink-0"
											/>
											<span>{asset.symbol}</span>
										</div>
									</Select.Option>
								))}
							</Select.Options>
						</Select.Root>
					)}

					{availableReceiveAssets.length > 0 && (
						<span className="typo-body-2xs">
							Once the transfer is complete, you&apos;ll receive{' '}
							{selectedReceiveAsset.symbol} directly in your Velocity account.
						</span>
					)}
				</div>

				{/* Copy Address */}
				<div className="flex flex-col gap-3">
					<span className="typo-label-xs text-text-default">
						{createNewSubaccount ? '3' : '4'}. Copy Address
					</span>

					{availableReceiveAssets.length === 0 ? (
						<div className="rounded-sm border border-input-border bg-input-bg p-md">
							Select an account that supports USDC or SOL to generate a deposit
							address.
						</div>
					) : (
						<>
							<div className="flex gap-3">
								{/* QR Code */}
								<div className="relative flex size-32 shrink-0 items-center justify-center rounded-sm bg-white p-2">
									{receivingAddress && !isAddressLoading ? (
										<QRCode
											value={receivingAddress}
											size={122}
											level="H"
											includeMargin={false}
										/>
									) : (
										<div className="bg-container-bg-secondary h-full w-full animate-pulse rounded" />
									)}
									{/* Chain icon overlay */}
									{selectedChain && getChainIconSymbol(selectedChain.id) && (
										<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
											<MarketIcon
												marketSymbol={
													getChainIconSymbol(selectedChain.id) ?? ''
												}
												className="size-8"
											/>
										</div>
									)}
								</div>

								{/* Address Section */}
								<div className="flex flex-1 flex-col justify-between overflow-hidden rounded-sm border border-input-border bg-container-bg">
									<div className="flex flex-col gap-2 p-md">
										<DefinitionTooltip
											definitionId="depositFromOtherSourcesReceivingAddress"
											className="w-fit"
										>
											Receiving Address
										</DefinitionTooltip>

										{isAddressLoading ? (
											<InlineLoadingBar />
										) : (
											<span className="typo-body-sm break-all text-text-default">
												{receivingAddress}
											</span>
										)}
									</div>

									<Button
										variant="secondary"
										size="md"
										className="w-full rounded-none rounded-b-sm border-0 border-t border-input-border"
										disabled={isAddressLoading}
										onClick={handleCopy}
									>
										{copied ? (
											<>
												<span>Copied</span>
												<Checkmark size={16} />
											</>
										) : (
											<>
												<span>Copy</span>
												<Copy size={16} />
											</>
										)}
									</Button>
								</div>
							</div>
							<span className="typo-body-2xs">
								Only use this receiving address for this subaccount. It will
								also change depending on which assets you chose to receive in
								Velocity in step {createNewSubaccount ? '2' : '3'}.
							</span>
						</>
					)}
				</div>

				<Divider />

				{/* Info Section */}
				<div className="flex flex-col gap-1">
					<div className="flex items-center justify-between">
						<span>Processing Time</span>
						<span className="text-text-default">&lt; 1 min</span>
					</div>
					<div className="flex items-center justify-between">
						<DefinitionTooltip definitionId="depositFromOtherSourcesPriceImpact">
							Price Impact
						</DefinitionTooltip>
						<span className="text-text-default">
							{isGettingQuote
								? 'Estimating…'
								: quote?.estFeeBreakdown?.totalImpact !== undefined
									? `~${quote.estFeeBreakdown.totalImpact.toFixed(2)}%`
									: '-'}
						</span>
					</div>

					{(createNewSubaccount || userAccountNotInitialized) && (
						<div className="flex items-center justify-between">
							<span>Reclaimable account setup fee</span>
							<span className="text-text-default">
								~
								<NumberDisplay
									options={{
										digits: {
											kind: 'decimals',
											decimals: 2,
											rounding: 'truncate',
										},
										small: { mode: 'subscript', order: 'after-digits' },
										suffix: 'SOL',
									}}
									value={baseAccountRent}
								/>
							</span>
						</div>
					)}
				</div>
			</BaseModal.Body>
		</BaseModal.Container>
	);
};

export default DepositFromOtherSourcesModal;
